import { useEffect, useMemo, useState } from "react";

import {
  createAccountingReviewDocumentRequest,
  getAccountingReviewDocumentDownloadUrl,
  listAccountingReviewDocuments,
  updateAccountingReviewDocument,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";

const statusClasses = {
  upload_requested: "border-amber-200 bg-amber-50 text-amber-700",
  received: "border-emerald-200 bg-emerald-50 text-emerald-700",
  being_reviewed: "border-sky-200 bg-sky-50 text-sky-700",
  needs_new_copy: "border-rose-200 bg-rose-50 text-rose-700",
  done: "border-slate-200 bg-slate-100 text-slate-700",
};

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatSize(value) {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return "Size pending";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SalonBusinessCpaReviewQueue() {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [requestClientNote, setRequestClientNote] = useState(
    "Please upload a clearer copy so we can continue the review."
  );
  const [requestDueDate, setRequestDueDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const counts = useMemo(() => {
    return documents.reduce(
      (next, document) => {
        next.total += 1;
        if (document.status === "needs_new_copy") next.needsNewCopy += 1;
        if (document.status === "received") next.received += 1;
        if (document.status === "being_reviewed") next.reviewing += 1;
        if (document.status === "done") next.done += 1;
        return next;
      },
      { total: 0, received: 0, reviewing: 0, needsNewCopy: 0, done: 0 }
    );
  }, [documents]);

  const loadDocuments = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listAccountingReviewDocuments(token, {
        status: statusFilter || undefined,
        document_type: typeFilter || undefined,
        limit: 150,
      });
      setDocuments(Array.isArray(data?.documents) ? data.documents : []);
      setStatusOptions(Array.isArray(data?.status_options) ? data.status_options : []);
      setTypeOptions(Array.isArray(data?.document_type_options) ? data.document_type_options : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load the review queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusFilter, typeFilter]);

  const handleStatusChange = async (documentId, nextStatus) => {
    setBusyId(documentId);
    setError("");
    setNotice("");
    try {
      const updated = await updateAccountingReviewDocument(token, documentId, {
        status: nextStatus,
        review_note: reviewNote,
      });
      setDocuments((current) =>
        current.map((document) => (document.id === documentId ? updated : document))
      );
      setReviewNote("");
      setNotice("Document status updated.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update document status.");
    } finally {
      setBusyId(null);
    }
  };

  const handleOpenDocument = async (documentId) => {
    setBusyId(documentId);
    setError("");
    setNotice("");
    try {
      const data = await getAccountingReviewDocumentDownloadUrl(token, documentId);
      if (data?.download_url) {
        window.open(data.download_url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to open this document.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRequestNewCopy = async (document) => {
    setBusyId(document.id);
    setError("");
    setNotice("");
    try {
      await createAccountingReviewDocumentRequest(token, {
        source_document_id: document.id,
        title: `New copy needed: ${document.file_name}`,
        client_note: requestClientNote,
        internal_note: reviewNote,
        due_date: requestDueDate || null,
      });
      setDocuments((current) =>
        current.map((item) =>
          item.id === document.id
            ? { ...item, status: "needs_new_copy", status_label: "Need a new copy" }
            : item
        )
      );
      setNotice("New copy request created.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to create the document request.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SalonBusinessCpaShell
      title="Review queue"
      description="Review uploaded accounting, tax, payroll, GST/HST, bookkeeping, and year-end records."
      actions={
        <button
          type="button"
          onClick={loadDocuments}
          className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Refresh
        </button>
      }
    >
      <section className="grid gap-4 md:grid-cols-5">
        {[
          ["Total", counts.total],
          ["Received", counts.received],
          ["Reviewing", counts.reviewing],
          ["Need new copy", counts.needsNewCopy],
          ["Done", counts.done],
        ].map(([label, value]) => (
          <div key={label} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              {label}
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{value}</p>
          </div>
        ))}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="grid gap-4 md:grid-cols-[180px_220px_1fr]">
          <label className="text-sm font-semibold text-slate-700">
            Status
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
            >
              <option value="">All statuses</option>
              <option value="upload_requested">Upload started</option>
              {statusOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Document type
            <select
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
            >
              <option value="">All types</option>
              {typeOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Review note, optional
            <input
              value={reviewNote}
              onChange={(event) => setReviewNote(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
              placeholder="Example: requested clearer bank statement"
            />
          </label>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-[1fr_180px]">
          <label className="text-sm font-semibold text-slate-700">
            Client note for new copy requests
            <input
              value={requestClientNote}
              onChange={(event) => setRequestClientNote(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
              placeholder="Please upload all pages of the statement."
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Due date
            <input
              type="date"
              value={requestDueDate}
              onChange={(event) => setRequestDueDate(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
            />
          </label>
        </div>
        {error ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}
        {notice ? (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            {notice}
          </div>
        ) : null}
      </section>

      <section className="space-y-3">
        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
            Loading review queue...
          </div>
        ) : documents.length ? (
          documents.map((document) => (
            <article
              key={document.id}
              className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-slate-950">
                      {document.file_name}
                    </h2>
                    <span
                      className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                        statusClasses[document.status] ||
                        "border-slate-200 bg-slate-100 text-slate-700"
                      }`}
                    >
                      {document.status_label}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {document.business_name || "Business"} / {document.salon_name || "Salon"}
                  </p>
                  <p className="text-sm leading-6 text-slate-600">
                    {document.document_type_label} - {formatSize(document.file_size)} - Uploaded {formatDate(document.created_at_utc)}
                  </p>
                  <p className="text-sm leading-6 text-slate-600">
                    Uploaded by {document.uploaded_by_name || document.uploaded_by_email || "Unknown user"}
                  </p>
                  {document.note ? (
                    <p className="mt-2 rounded-2xl bg-slate-50 p-3 text-sm text-slate-600">
                      {document.note}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2 lg:justify-end">
                  <button
                    type="button"
                    onClick={() => handleOpenDocument(document.id)}
                    disabled={busyId === document.id}
                    className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  >
                    Open document
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRequestNewCopy(document)}
                    disabled={busyId === document.id}
                    className="rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                  >
                    Request new copy
                  </button>
                  {statusOptions.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      onClick={() => handleStatusChange(document.id, option.code)}
                      disabled={busyId === document.id || document.status === option.code}
                      className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </article>
          ))
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
            No documents match these filters.
          </div>
        )}
      </section>
    </SalonBusinessCpaShell>
  );
}
